/**
 * `<EditorFontSettings>` — the editor-content font picker (2026-06-12, Chris ask).
 *
 * Choose the face the note reading/writing surface renders in, from the curated
 * ten in `theme/editorFonts.ts` (3 tech / 3 business / 4 other). Per-device
 * preference (localStorage), applied live via `--font-editor` — same pattern as
 * the UI scale. The rest of the type system (chrome / data / identity voices)
 * is fixed and unaffected.
 *
 * Every option row renders in its own face (the catalogue stylesheet is lazily
 * loaded on panel mount), and a live preview paragraph shows the current pick.
 */

import { useEffect, useState } from 'react';
import {
  EDITOR_FONTS,
  DEFAULT_EDITOR_FONT_ID,
  applyEditorFont,
  ensureAllEditorFontsLoaded,
  getEditorFont,
  loadSavedEditorFont,
  type EditorFontCategory,
} from '@/theme/editorFonts';
import { SettingsCard } from './SettingsCard';

const CATEGORY_LABELS: Record<EditorFontCategory, string> = {
  tech: 'Tech',
  business: 'Business',
  editorial: 'Reading & editorial',
};

const PREVIEW_TEXT =
  'The quick brown fox jumps over the lazy dog — 0123456789. ' +
  'Aletheia keeps every meeting, decision, and idea retrievable.';

export function EditorFontSettings(): JSX.Element {
  const [selected, setSelected] = useState<string>(() => loadSavedEditorFont());

  // Load all ten faces once so the option rows + preview render true to face.
  useEffect(() => {
    ensureAllEditorFontsLoaded();
  }, []);

  const pick = (id: string): void => {
    applyEditorFont(id);
    setSelected(id);
  };

  const current = getEditorFont(selected);

  return (
    <SettingsCard
      id="editor-font"
      title="Editor font"
      desc="The typeface notes are written and read in. Per-device; applies instantly."
    >
      <div className="settings-stack">
        {/* U9: one chip cloud — three stacked category groups made this card
            ~475px tall beside a 160px neighbor. The catalogue order already
            groups tech → business → editorial, and each chip's tooltip names
            its category + hint. */}
        <div className="settings-options">
          {EDITOR_FONTS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="editor-font-option"
              aria-pressed={f.id === selected}
              title={`${CATEGORY_LABELS[f.category]} — ${f.hint}`}
              /* U4: font-family is data-driven (per-option face) — can't be a class. */
              style={{ fontFamily: f.stack }}
              onClick={() => pick(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Live preview in the selected face (data-driven font — see above).
            U9: one line — the pick's hint already shows as its tooltip. */}
        <div className="editor-font-preview" style={{ fontFamily: current.stack }}>
          <strong>{current.label}</strong> — {PREVIEW_TEXT}
        </div>

        {selected !== DEFAULT_EDITOR_FONT_ID && (
          <div>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => pick(DEFAULT_EDITOR_FONT_ID)}
            >
              Reset to default (IBM Plex Sans)
            </button>
          </div>
        )}
      </div>
    </SettingsCard>
  );
}
